import { useEffect, useRef, useState } from 'react';
import { getPassengers } from 'src/api/passengerMangerApi/passengerMangerApi';
import useGetApiRequestBasicParams from 'src/hooks/useGetApiRequestBasicParams';
import { useRootAppSelector } from 'src/store/hooks';
import { screenClientCodeSelector } from 'src/store/slices/manualOrders/manualOrdersSlice';
import { SUCCESS } from '../PlacementAddressSelection/helpers.PlacementAddressSelection';
import helpers, { IndexedAddress } from './helpers.PassengersAddressesMatrix';

/** At most this many ws_MyWayStudio_Get_Passengers_Details calls run at once. */
const MAX_PARALLEL_REQUESTS = 4;

type FetchStatus = 'loading' | 'loaded' | 'failed';

/**
 * Fetches the addresses of every selected passenger, once per passenger while the form is open.
 * @param {string[]} passIds - the selected passengers
 */
const usePassengersAddresses = (passIds: string[]) => {
    const clientCode = useRootAppSelector(screenClientCodeSelector);
    const { token, dbUrl, proxyUrl } = useGetApiRequestBasicParams();

    const [addressesByPassenger, setAddressesByPassenger] = useState<Record<string, IndexedAddress[]>>({});
    const [statusByPassenger, setStatusByPassenger] = useState<Record<string, FetchStatus>>({});

    // Passengers already requested, so a re-render never fetches one twice; a failed one is removed to retry
    const requestedRef = useRef(new Set<string>());
    const clientCodeRef = useRef(clientCode);
    const isMountedRef = useRef(true);
    const canFetch = !!(token && dbUrl && clientCode);

    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        []
    );

    const passIdsKey = passIds.join(',');

    useEffect(() => {
        if (!canFetch) return;

        // Another client's addresses are not this client's: start over
        if (clientCodeRef.current !== clientCode) {
            clientCodeRef.current = clientCode;
            requestedRef.current = new Set<string>();
            setAddressesByPassenger({});
            setStatusByPassenger({});
        }

        const missing = passIds.filter((passId) => !requestedRef.current.has(passId));
        if (!missing.length) return;

        missing.forEach((passId) => requestedRef.current.add(passId));
        setStatusByPassenger((prev) => {
            const next = { ...prev };
            missing.forEach((passId) => {
                next[passId] = 'loading';
            });
            return next;
        });

        helpers
            .runWithConcurrency(missing, MAX_PARALLEL_REQUESTS, async (passId) => {
                try {
                    const res = await getPassengers({
                        token,
                        dbUrl,
                        clientCode,
                        passCode: passId,
                        proxyUrl: proxyUrl || '',
                    });

                    if (String(res?.data?.response) !== SUCCESS) return { passId, addresses: null };

                    const passenger = res.data.passList?.[0];
                    return { passId, addresses: helpers.toIndexedAddresses(passenger?.address) };
                } catch (error) {
                    console.log(error);
                    return { passId, addresses: null };
                }
            })
            .then((results) => {
                results.forEach(({ passId, addresses }) => {
                    if (!addresses) requestedRef.current.delete(passId);
                });

                if (!isMountedRef.current) return;

                setAddressesByPassenger((prev) => {
                    const next = { ...prev };
                    results.forEach(({ passId, addresses }) => {
                        if (addresses) next[passId] = addresses;
                    });
                    return next;
                });
                setStatusByPassenger((prev) => {
                    const next = { ...prev };
                    results.forEach(({ passId, addresses }) => {
                        next[passId] = addresses ? 'loaded' : 'failed';
                    });
                    return next;
                });
            });
        // passIds is represented by passIdsKey
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passIdsKey, token, dbUrl, proxyUrl, clientCode, canFetch]);

    // Without a session nothing is fetched, so nothing is loading: the matrix shows "no addresses"
    const isLoading =
        canFetch &&
        passIds.some((passId) => !statusByPassenger[passId] || statusByPassenger[passId] === 'loading');
    const failedPassIds = passIds.filter((passId) => statusByPassenger[passId] === 'failed');

    return { addressesByPassenger, isLoading, failedPassIds };
};

const passengersAddressesMatrixHooks = { usePassengersAddresses };

export default passengersAddressesMatrixHooks;
